import {
  Badge,
  Container,
  KeyValuePairs,
  KeyValuePairsProps,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import { DigitalBusinessDocument } from "../../../types";
import {
  getFulfillmentStatus,
  getFulfillmentText,
  getPaymentStatus,
  getPaymentStatusText,
  getRefundStatus,
  hasCostPayment,
  hasCostRefunds,
  hasCosts,
  hasSettlementDetails,
  isRevenueBookingsEnabled,
  screamingSnakeToTitleCase,
} from "../../../helpers/dbdUtils";
import DigitalOrderSourceLink from "../../common/DigitalOrderSourceLink";
import { PromotionStatus } from "../../common/PromotionStatus";
import Item = KeyValuePairsProps.Item;

interface SummarySectionProps {
  dbd: DigitalBusinessDocument;
}

export function SummarySection({ dbd }: SummarySectionProps) {
  const { body } = dbd.body;

  const getPaymentMethod = () => {
    const paymentId = Object.keys(body?.payments || {})[0];
    const payment = body?.payments?.[paymentId];
    return payment?.paymentMethods?.[paymentId];
  };

  const getFirstProduct = () => {
    return Object.values(body?.products || {})[0];
  };
  const method = getPaymentMethod();
  const firstProduct = getFirstProduct();
  const isManualRedrive =
    String(
      dbd.body?.metadata?.internalAttributes?.MANUAL_REDRIVE
    ).toLowerCase() === "true";

  const items: Item[] = [
    {
      label: "Created",
      value: (
        <SpaceBetween direction="horizontal" size="xs">
          {dbd.creationDate
            ? new Date(dbd.creationDate).toLocaleString()
            : "Unknown"}
          {isManualRedrive && <Badge color="grey">Manual Redrive</Badge>}
        </SpaceBetween>
      ),
    },
    {
      label: "Fulfillment Type",
      value: screamingSnakeToTitleCase(
        dbd.body.metadata.internalAttributes?.FULFILLMENT_TYPE || "IMMEDIATE"
      ),
    },
    {
      label: "Payment Status",
      value: method ? (
        <StatusIndicator type={getPaymentStatus(method)}>
          {getPaymentStatusText(method)}
        </StatusIndicator>
      ) : (
        <StatusIndicator type="stopped">No Payment Data</StatusIndicator>
      ),
    },
    {
      label: "Refund Status",
      value: (() => {
        const status = getRefundStatus(method);
        return (
          <StatusIndicator type={status.type}>{status.text}</StatusIndicator>
        );
      })(),
    },
    {
      label: "Settlement Details",
      value: (
        <StatusIndicator
          type={hasSettlementDetails(body?.payments) ? "success" : "stopped"}
        >
          {hasSettlementDetails(body?.payments)
            ? "Has Settlement Details"
            : "No Settlement Details"}
        </StatusIndicator>
      ),
    },
    {
      label: "Fulfillment Status",
      value: firstProduct ? (
        <StatusIndicator type={getFulfillmentStatus(firstProduct)}>
          {getFulfillmentText(firstProduct)}
        </StatusIndicator>
      ) : (
        <StatusIndicator type="stopped">No Product Data</StatusIndicator>
      ),
    },
    {
      label: "Promotions",
      value: firstProduct ? (
        (() => {
          const firstPromoId = Object.values(firstProduct.bills)
            .map((bill) => Object.keys(bill.promotions || {}))
            .find((promoIds) => promoIds.length > 0)?.[0];

          return <PromotionStatus promotionId={firstPromoId} />;
        })()
      ) : (
        <StatusIndicator type="stopped">No Product Data</StatusIndicator>
      ),
    },
    {
      label: "Cost Status",
      value: firstProduct ? (
        <StatusIndicator type={hasCosts(firstProduct) ? "success" : "stopped"}>
          {hasCosts(firstProduct) ? "Has Cost" : "No Cost"}
        </StatusIndicator>
      ) : (
        <StatusIndicator type="stopped">No Product Data</StatusIndicator>
      ),
    },
    {
      label: "Cost Payment Status",
      value: firstProduct ? (
        <StatusIndicator
          type={hasCostPayment(firstProduct.costs) ? "success" : "stopped"}
        >
          {hasCostPayment(firstProduct.costs)
            ? "Has Cost Payment Info"
            : "No Cost Payment Info"}
        </StatusIndicator>
      ) : (
        <StatusIndicator type="stopped">No Product Data</StatusIndicator>
      ),
    },
    {
      label: "Cost Refund Status",
      value: firstProduct ? (
        <StatusIndicator
          type={hasCostRefunds(firstProduct.costs) ? "success" : "stopped"}
        >
          {hasCostRefunds(firstProduct.costs)
            ? "Has Cost Refund"
            : "No Cost Refund"}
        </StatusIndicator>
      ) : (
        <StatusIndicator type="stopped">No Product Data</StatusIndicator>
      ),
    },
    {
      label: "Digital Order Source",
      value: (
        <DigitalOrderSourceLink
          source={dbd.body.metadata.attributes?.digital_order_source}
          event={dbd}
        />
      ),
    },
    {
      label: "Revenue Bookings",
      value: (
        <StatusIndicator
          type={
            isRevenueBookingsEnabled(dbd.body.metadata) ? "success" : "stopped"
          }
        >
          {isRevenueBookingsEnabled(dbd.body.metadata)
            ? "Enabled"
            : "Disabled (Royalty Only)"}
        </StatusIndicator>
      ),
    },
  ];

  return (
    <Container>
      <KeyValuePairs columns={2} items={items} />
    </Container>
  );
}
